import { DEFAULT_PLAN_ID, getPlanById } from './plans.js';
import {
  loginRequest,
  registerRequest,
  currentUserRequest,
  logoutRequest,
  listUsersRequest,
  createUserRequest,
  updateUserRequest,
  deleteUserRequest,
} from './api.js';
import { getAuthToken, setAuthToken, clearAuthToken } from './session.js';
import { disableDemoMode, enableDemoMode, isDemoMode } from './demo/runtime.js';
import { ensureDemoState } from './demo/store.js';

let currentUser = null;
let authReadyPromise = null;
const UI_VERSION_VALUES = new Set([1, 2, 3]);

const normalizeUiVersion = (value) => {
  if (typeof value === 'number' && Number.isInteger(value) && UI_VERSION_VALUES.has(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      const parsed = Number(trimmed);
      if (Number.isInteger(parsed) && UI_VERSION_VALUES.has(parsed)) {
        return parsed;
      }
    }
  }
  return 1;
};

const notifyAuthChange = (user) => {
  window.dispatchEvent(new CustomEvent('anforderungsai:auth-changed', { detail: user }));
};

const toProfile = (profile) => ({
  firstName: profile?.firstName ?? '',
  lastName: profile?.lastName ?? '',
  company: profile?.company ?? '',
  phone: profile?.phone ?? '',
  vatNumber: profile?.vatNumber ?? '',
  address: {
    street: profile?.address?.street ?? '',
    postalCode: profile?.address?.postalCode ?? '',
    city: profile?.address?.city ?? '',
    country: profile?.address?.country ?? '',
  },
});

const toJobStats = (stats) => {
  const total = Number(stats?.total ?? 0);
  const currentMonth = Number(stats?.currentMonth ?? 0);
  const monthly = Array.isArray(stats?.monthly)
    ? stats.monthly
        .filter((entry) => entry && typeof entry.month === 'string')
        .map((entry) => ({
          month: entry.month,
          count: Number(entry.count ?? 0),
        }))
    : [];
  return { total, currentMonth, monthly };
};

const composeUser = (user) => {
  if (!user) return null;
  const normalizedEmail = user.email?.trim().toLowerCase() ?? '';
  const plan = getPlanById(user.planId) ?? getPlanById(DEFAULT_PLAN_ID);
  const role = user.role ?? 'user';
  return {
    email: normalizedEmail,
    uiVersion: normalizeUiVersion(user.uiVersion),
    planId: plan?.id ?? DEFAULT_PLAN_ID,
    plan,
    billing: user.billing ?? null,
    billingStatus: user.billingStatus ?? null,
    stripeCustomerId: user.stripeCustomerId ?? null,
    stripeSubscriptionId: user.stripeSubscriptionId ?? null,
    stripeSubscriptionItemId: user.stripeSubscriptionItemId ?? null,
    stripePriceId: user.stripePriceId ?? null,
    pendingPlanId: user.pendingPlanId ?? null,
    paygInvoiceMode: user.paygInvoiceMode ?? 'per_use',
    freeUsageState: user.freeUsageState ?? 'available',
    freeUsageReservationConversationId: user.freeUsageReservationConversationId ?? null,
    packageMPeriod: user.packageMPeriod ?? null,
    packageMIncludedUsed: Number(user.packageMIncludedUsed ?? 0),
    packageMTopupRemaining: Number(user.packageMTopupRemaining ?? 0),
    customerProvisioning: user.customerProvisioning ?? null,
    pendingStartRetryToken: user.pendingStartRetryToken ?? null,
    role,
    status: user.status ?? 'active',
    archivedAt: user.archivedAt ?? null,
    profile: toProfile(user.profile),
    jobStats: toJobStats(user.jobStats),
    createdAt: user.createdAt ?? null,
    updatedAt: user.updatedAt ?? null,
  };
};

const setCurrentUser = (user) => {
  currentUser = composeUser(user);
  notifyAuthChange(currentUser);
  return currentUser;
};

const clearCurrentUser = () => {
  currentUser = null;
  notifyAuthChange(null);
};

const handleSessionExpired = () => {
  const wasDemo = isDemoMode();
  clearAuthToken();
  if (wasDemo) {
    disableDemoMode();
  }
  clearCurrentUser();
  authReadyPromise = null;
  if (window.location.hash !== '#/login') {
    window.location.hash = '#/login';
  }
};

window.addEventListener('anforderungsai:session-expired', handleSessionExpired);

const bootstrapAuth = () => {
  if (authReadyPromise) {
    return authReadyPromise;
  }
  let token = getAuthToken();
  if (!token && isDemoMode()) {
    setAuthToken('demo');
    token = 'demo';
  }
  if (!token) {
    authReadyPromise = Promise.resolve(null);
    return authReadyPromise;
  }
  authReadyPromise = currentUserRequest()
    .then((response) => setCurrentUser(response?.user ?? null))
    .catch((error) => {
      if (error?.code === 'UNAUTHORIZED') {
        clearAuthToken();
      } else {
        console.warn('Failed to refresh session', error);
      }
      clearCurrentUser();
      return null;
    });
  return authReadyPromise;
};

export const whenAuthReady = () => bootstrapAuth();

export const getCurrentUser = () => currentUser;

export const login = async ({ email, password }) => {
  const result = await loginRequest({ email, password });
  if (!result?.token || !result?.user) {
    throw new Error('Unerwartete Antwort vom Server.');
  }
  setAuthToken(result.token);
  return setCurrentUser(result.user);
};

export const register = async (payload) => {
  const result = await registerRequest(payload);
  if (!result?.token || !result?.user) {
    throw new Error('Unerwartete Antwort vom Server.');
  }
  setAuthToken(result.token);
  return setCurrentUser(result.user);
};

export const logout = async () => {
  const wasDemo = isDemoMode();
  const token = getAuthToken();
  clearAuthToken();
  clearCurrentUser();
  authReadyPromise = null;
  if (wasDemo) {
    disableDemoMode();
    window.location.hash = '#/';
    window.location.reload();
    return;
  }
  if (token) {
    try {
      await logoutRequest();
    } catch (error) {
      if (error?.code !== 'UNAUTHORIZED') {
        console.warn('Logout request failed', error);
      }
    }
  }
};

export const startDemoSession = async () => {
  enableDemoMode();
  ensureDemoState();
  setAuthToken('demo');
  const response = await currentUserRequest();
  setCurrentUser(response?.user ?? null);
  window.location.hash = '#/dashboard';
  return getCurrentUser();
};

const updateCurrentUserIfMatch = (updatedUser) => {
  if (!updatedUser) return;
  if (!currentUser) return;
  if (updatedUser.email?.toLowerCase() === currentUser.email?.toLowerCase()) {
    setCurrentUser(updatedUser);
  }
};

export const refreshCurrentUser = async () => {
  try {
    const response = await currentUserRequest();
    return setCurrentUser(response?.user ?? null);
  } catch (error) {
    if (error?.code === 'UNAUTHORIZED') {
      clearAuthToken();
      clearCurrentUser();
      return null;
    }
    console.warn('Failed to refresh user profile', error);
    throw error;
  }
};

export const listUsers = async () => {
  const response = await listUsersRequest();
  const users = Array.isArray(response?.users) ? response.users : [];
  return users.map(composeUser);
};

export const createUser = async (payload) => {
  const response = await createUserRequest(payload);
  return composeUser(response?.user ?? null);
};

export const updateUserPlan = async ({
  email,
  planId,
  clearBilling = false,
}) => {
  const response = await updateUserRequest(email, {
    planId,
    clearBilling,
  });
  updateCurrentUserIfMatch(response?.user);
  return composeUser(response?.user ?? null);
};

export const updateUserRole = async ({ email, role }) => {
  const response = await updateUserRequest(email, { role });
  updateCurrentUserIfMatch(response?.user);
  return composeUser(response?.user ?? null);
};

export const updateUserUiVersion = async ({ email, uiVersion }) => {
  const response = await updateUserRequest(email, { uiVersion });
  updateCurrentUserIfMatch(response?.user);
  return composeUser(response?.user ?? null);
};

export const updateUserPassword = async ({ email, password, currentPassword = '' }) => {
  const payload = { password };
  if (typeof currentPassword === 'string' && currentPassword) {
    payload.currentPassword = currentPassword;
  }
  const response = await updateUserRequest(email, payload);
  updateCurrentUserIfMatch(response?.user);
  return composeUser(response?.user ?? null);
};

export const updateUserProfile = async ({
  email,
  firstName,
  lastName,
  company,
  phone,
  vatNumber,
  street,
  postalCode,
  city,
  country,
}) => {
  const profile = {
    firstName,
    lastName,
    company,
    vatNumber,
    street,
    postalCode,
    city,
    country,
  };
  if (typeof phone === 'string') {
    profile.phone = phone;
  }
  const response = await updateUserRequest(email, { profile });
  updateCurrentUserIfMatch(response?.user);
  return composeUser(response?.user ?? null);
};

export const deleteUser = async (email) => {
  const response = await deleteUserRequest(email);
  updateCurrentUserIfMatch(response?.user);
  return composeUser(response?.user ?? null);
};

// Role helpers
export const isAdmin = (user = currentUser) => {
  return user?.role === 'admin';
};

export const isAppEntwickler = (user = currentUser) => {
  return user?.role === 'appEntwickler';
};

export const isAppEntwicklerOrAdmin = (user = currentUser) => {
  return isAdmin(user) || isAppEntwickler(user);
};

export const hasAppFactoryAccess = () => {
  return isAppEntwicklerOrAdmin();
};

// Initialize on module load.
bootstrapAuth();
